import {
  OpenAPIV3_1,
} from 'openapi-types';
import {
  Ref,
  computed, ref, watch,
} from 'vue';
import {
  useRoute, useRouter,
} from 'vue-router';

import {
  isJSONEqual,
} from '../utils/object';

export type TParamDefinition<T> = {
  default: T,
  parse: (value: unknown) => T,
  serialize: (value: T | undefined) => string | undefined,
};
export type TParamSchema = Record<string, TParamDefinition<any>>;
export type TState<T extends TParamSchema> = {
  [K in keyof T]: ReturnType<T[K]['parse']>
};

export function stringParam<T extends string = string>(defaultValue?: T): TParamDefinition<T | undefined> {
  return {
    default: defaultValue,
    parse: (v) => String(v) as T,
    serialize: (v) => (v || undefined),
  };
}

export function numberParam<T extends number = number>(defaultValue?: T): TParamDefinition<T | undefined> {
  return {
    default: defaultValue,
    parse: (v) => Number(v) as T,
    serialize: (v) => (v !== undefined ? v.toString() : undefined),
  };
}

export function booleanParam(defaultValue?: boolean): TParamDefinition<boolean | undefined> {
  return {
    default: defaultValue,
    parse: (v) => v === 'true',
    serialize: (v) => (
      v !== undefined
        ? (v ? 'true' : 'false')
        : undefined
    ),
  };
}

function parseArray<T>(value: unknown, itemParser: (v: unknown) => T): T[] {
  const itemArray = Array.isArray(value) ? value : String(value).split(',');
  return itemArray.filter(Boolean).map(itemParser);
}

function serializeArray<T>(value: T[] | undefined): string | undefined {
  return (value !== undefined && value.length > 0 ? value.map(String).join(',') : undefined);
}

export function stringArrayParam<T extends string = string>(defaultValue: T[] = []): TParamDefinition<T[]> {
  return {
    default: [
      ...defaultValue,
    ],
    parse: (v) => parseArray<T>(v, String as unknown as (v: unknown) => T),
    serialize: (v) => serializeArray(v),
  };
}

export function numberArrayParam(defaultValue: number[] = []): TParamDefinition<number[]> {
  return {
    default: [
      ...defaultValue,
    ],
    parse: (v) => parseArray(v, Number),
    serialize: (v) => serializeArray(v),
  };
}

export function useStateUrl<T extends TParamSchema>(schema: T) {
  const route = useRoute();
  const router = useRouter();

  const state = computed<TState<T>>(() => {
    return Object.keys(schema).reduce(
      (acc, key) => {
        const paramDefinition = schema[key];
        const queryValue = route.query[key];

        acc[key as keyof TState<T>] = queryValue !== undefined
          ? paramDefinition.parse(queryValue)
          : paramDefinition.default;
        return acc;
      },
      {} as TState<T>,
    );
  });

  function setQuery(newState: TState<T>) {
    // make sure to extend the existing query, not overwrite
    const newQuery = {
      ...route.query,
    };

    Object.keys(schema).forEach((key) => {
      const parser = schema[key];
      const value = newState[key];

      const serialized = parser.serialize(value as any);

      if (value === undefined || serialized === undefined) {
        delete newQuery[key];
      } else {
        newQuery[key] = serialized;
      }
    });

    return router.replace({
      query: newQuery,
    });
  }

  return {
    state,
    setQuery,
  };
}

type TUseUrlSyncedRefComposable<T extends TParamSchema> = {
  state: Ref<TState<T>>,
  resetToDefault: () => void,
};

export function useUrlSyncedRef<T extends TParamSchema>(schema: T): TUseUrlSyncedRefComposable<T> {
  const {
    state: queryState, setQuery,
  } = useStateUrl(schema);

  // ⚠️ This needs to be a ref because consumers will modify this state object without reassigning.
  // If this was a shallowRef this kind of required reactivity would be missing.
  const stateRef = ref<TState<T>>({
    ...queryState.value,
  });

  // Watch changes of query params in route
  watch(
    queryState,
    (newVal) => {
      if (isJSONEqual(newVal, stateRef.value)) {
        return;
      }
      stateRef.value = {
        ...newVal,
      };
    },
    {
      immediate: true,
    },
  );

  // watch changes to ref made outside by the component
  watch(
    stateRef,
    (newState) => {
      if (isJSONEqual(newState, queryState.value)) {
        return;
      }
      return setQuery(newState);
    },
    {
      deep: true,
    },
  );

  function resetToDefault() {
    stateRef.value = Object.keys(schema).reduce(
      (acc, key) => {
        acc[key as keyof TState<T>] = schema[key].default;
        return acc;
      },
      {} as TState<T>,
    );
  }

  return {
    // @ts-expect-error - seems like a false positive
    state: stateRef,
    resetToDefault,
  };
}

function resolveSchemaType(schema: OpenAPIV3_1.SchemaObject): string | undefined {
  if (Array.isArray(schema.type)) {
    return schema.type.find((schemaTypeName: string) => schemaTypeName !== 'null');
  }
  return schema.type;
}

function isConcretSchema(candidate: OpenAPIV3_1.SchemaObject | OpenAPIV3_1.ReferenceObject): candidate is OpenAPIV3_1.SchemaObject {
  return typeof candidate !== 'boolean' && !('$ref' in candidate);
}

function isNullSchema(schema: OpenAPIV3_1.SchemaObject): boolean {
  const schemaType = resolveSchemaType(schema);
  return schemaType === 'null';
}

/**
 * Resolves the effective concrete schema from a schema that may use `anyOf` or `allOf`.
 * For `anyOf`, returns the first non-null, non-reference candidate (i.e. the actual value type).
 * For `allOf`, returns the first candidate that carries an explicit type.
 * Falls back to the schema itself when neither keyword is present.
 */
function resolveEffectiveSchema(schema: OpenAPIV3_1.SchemaObject): OpenAPIV3_1.SchemaObject {
  if (schema.anyOf) {
    const nonNullCandidate = schema.anyOf.find(
      (candidate) => isConcretSchema(candidate) && !isNullSchema(candidate),
    );
    if (nonNullCandidate && isConcretSchema(nonNullCandidate)) {
      return resolveEffectiveSchema(nonNullCandidate);
    }
  }

  if (schema.allOf) {
    const typedCandidate = schema.allOf.find(
      (candidate) => isConcretSchema(candidate) && resolveSchemaType(candidate) !== undefined,
    );
    if (typedCandidate && isConcretSchema(typedCandidate)) {
      return resolveEffectiveSchema(typedCandidate);
    }
  }

  return schema;
}

export function buildSchemaFromJsonSchema(jsonSchema: OpenAPIV3_1.SchemaObject): TParamSchema {
  const properties = jsonSchema.properties ?? {};

  return Object.entries(properties).reduce(
    (acc, [
      key,
      propertySchema,
    ]) => {
      if (typeof propertySchema === 'boolean' || '$ref' in propertySchema) {
        return acc;
      }

      const effectiveSchema = resolveEffectiveSchema(propertySchema);
      const schemaType = resolveSchemaType(effectiveSchema);
      const defaultValue = propertySchema.default;

      switch (schemaType) {
        case 'array': {
          const items = (effectiveSchema as OpenAPIV3_1.ArraySchemaObject).items;
          const itemSchema = (typeof items === 'object' && items !== null && !('$ref' in items))
            ? resolveEffectiveSchema(items as OpenAPIV3_1.SchemaObject)
            : undefined;
          const itemType = itemSchema ? resolveSchemaType(itemSchema) : undefined;

          switch (itemType) {
            case 'integer':
            case 'number': {
              acc[key] = numberArrayParam(defaultValue);
              break;
            }
            case 'string': {
              acc[key] = stringArrayParam(defaultValue);
              break;
            }
            default: {
              throw new Error(`buildSchemaFromJsonSchema: unsupported array item type "${itemType}" for property "${key}". Add a matching *Param conversion.`);
            }
          }
          break;
        }
        case 'string': {
          acc[key] = stringParam(defaultValue);
          break;
        }
        case 'number':
        case 'integer': {
          acc[key] = numberParam(defaultValue);
          break;
        }
        case 'boolean': {
          acc[key] = booleanParam(defaultValue);
          break;
        }
        case 'object': {
          // useStateURL only works on flat objects not on nested objects, so we need to exclude nested objects
          break;
        }
        default: {
          throw new Error(`buildSchemaFromJsonSchema: unsupported schema type "${schemaType}" for property "${key}". Add a matching *Param conversion.`);
        }
      }

      return acc;
    },
    {} as TParamSchema,
  );
}
